(() => {
  if (window.ksCollectionFilterPanelV3Initialized) return;
  window.ksCollectionFilterPanelV3Initialized = true;

  let checkboxApplyTimer;
  const accordionClickState = new WeakMap();
  const previewParams = ['preview_theme_id', '_fd', 'pb'];

  const asElement = (target) => {
    if (target instanceof Element) return target;
    return target && target.parentElement ? target.parentElement : null;
  };

  const getPanel = (trigger) => {
    const section =
      (trigger && trigger.closest('.facest-filters-section')) ||
      document.querySelector('.facest-filters-section');
    const sidebar = section && section.querySelector('.m-sidebar');
    const content = sidebar && sidebar.querySelector('.m-sidebar--content');
    return { section, sidebar, content };
  };

  const getCollectionController = () =>
    window.MinimogTheme && window.MinimogTheme.Collection
      ? window.MinimogTheme.Collection
      : null;

  const refreshAccordionHeight = () => {
    const controller = getCollectionController();
    if (
      controller &&
      controller.accordions &&
      typeof controller.accordions.setContentHeight === 'function'
    ) {
      window.requestAnimationFrame(() => {
        try {
          controller.accordions.setContentHeight();
        } catch (error) {
          // Keep the panel usable even if Minimog's cached accordion is stale.
        }
      });
    }
  };

  const scheduleAccordionFallback = (button, item, content) => {
    const previous = {
      open: item.classList.contains('open'),
      height: content.style.height,
      expanded: button.getAttribute('aria-expanded'),
      hidden: content.getAttribute('aria-hidden'),
    };
    accordionClickState.set(item, previous);

    window.setTimeout(() => {
      const initial = accordionClickState.get(item);
      if (!initial) return;

      const changed =
        item.classList.contains('open') !== initial.open ||
        content.style.height !== initial.height ||
        button.getAttribute('aria-expanded') !== initial.expanded ||
        content.getAttribute('aria-hidden') !== initial.hidden;

      if (changed) return;

      const shouldOpen = !initial.open;
      item.classList.toggle('open', shouldOpen);
      button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      content.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
      content.style.opacity = '1';
      content.style.overflow = 'hidden';
      content.style.height = shouldOpen ? `${content.scrollHeight}px` : '0px';
      if (shouldOpen) refreshAccordionHeight();
    }, 50);
  };

  const openFilters = (trigger) => {
    const { section, sidebar, content } = getPanel(trigger);
    if (!section || !sidebar || !content) return;

    const controller = getCollectionController();
    try {
      if (controller && typeof controller.openSidebarFilter === 'function') {
        controller.openSidebarFilter();
      } else {
        sidebar.style.display = 'block';
      }
    } catch (error) {
      sidebar.style.display = 'block';
    }

    sidebar.style.display = 'block';
    section.classList.add('sidebar-open');
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty('--m-bg-opacity', '0.5');
      content.style.setProperty('--m-translate-x', '0');
      refreshAccordionHeight();
    });

    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  };

  const closeFilters = (trigger) => {
    const { section, sidebar, content } = getPanel(trigger);
    if (!section || !sidebar || !content) return;

    const controller = getCollectionController();
    try {
      if (controller && typeof controller.closeSidebarFilter === 'function') {
        controller.closeSidebarFilter();
      }
    } catch (error) {
      // The direct fallback below still closes the panel.
    }

    section.classList.remove('sidebar-open');
    sidebar.style.setProperty('--m-bg-opacity', '0');
    content.style.setProperty('--m-translate-x', '-100%');

    document
      .querySelectorAll('.ks-filter-open[aria-expanded="true"]')
      .forEach((button) => button.setAttribute('aria-expanded', 'false'));

    window.setTimeout(() => {
      const shouldHide =
        window.innerWidth < 1280 || sidebar.dataset.type === 'fixed';
      if (shouldHide && !section.classList.contains('sidebar-open')) {
        sidebar.style.removeProperty('display');
        sidebar.style.removeProperty('--m-bg-opacity');
      }
    }, 300);
  };

  const serializeFilterForm = (form) => {
    const params = new URLSearchParams(new FormData(form));
    params.delete('page');

    const currentParams = new URL(window.location.href).searchParams;
    previewParams.forEach((name) => {
      if (!currentParams.has(name) || params.has(name)) return;
      currentParams.getAll(name).forEach((value) => params.append(name, value));
    });

    return params;
  };

  const applyCheckboxFilters = (form) => {
    if (!form) return;

    const params = serializeFilterForm(form);
    const filterComponent = form.closest('collection-filters-form');

    if (
      filterComponent &&
      typeof filterComponent.renderPage === 'function'
    ) {
      try {
        filterComponent.renderPage(params.toString());
        return;
      } catch (error) {
        // Fall back to a normal navigation below.
      }
    }

    const url = new URL(window.location.href);
    url.search = params.toString();
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  window.ksOpenCollectionFilters = openFilters;
  window.ksCloseCollectionFilters = closeFilters;

  document.addEventListener(
    'click',
    (event) => {
      const target = asElement(event.target);
      if (!target) return;

      const accordionButton = target.closest(
        '.facest-filters-section .m-accordion--item-button'
      );
      if (accordionButton) {
        const item = accordionButton.closest('.m-accordion--item');
        const content = item && item.querySelector('.m-accordion--item-content');
        if (item && content) {
          scheduleAccordionFallback(accordionButton, item, content);
        }
      }

      const openButton = target.closest('.ks-filter-open');
      if (openButton) {
        event.preventDefault();
        event.stopPropagation();
        openFilters(openButton);
        return;
      }

      const closeButton = target.closest(
        '.facest-filters-section .m-sidebar--close'
      );
      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        closeFilters(closeButton);
        return;
      }

      const sidebar = target.closest('.facest-filters-section .m-sidebar');
      if (sidebar && target === sidebar) {
        event.preventDefault();
        event.stopPropagation();
        closeFilters(sidebar);
      }
    },
    true
  );

  document.addEventListener(
    'input',
    (event) => {
      const target = asElement(event.target);
      if (
        !target ||
        !target.matches('#CollectionFiltersForm input[type="checkbox"]') ||
        target.disabled
      ) {
        return;
      }

      // Prevent Minimog's stale form listener from swallowing or duplicating
      // checkbox submissions after the collection section is re-rendered.
      event.stopPropagation();
      window.clearTimeout(checkboxApplyTimer);
      const form = target.form || target.closest('form');
      checkboxApplyTimer = window.setTimeout(
        () => applyCheckboxFilters(form),
        300
      );
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const { section } = getPanel();
    if (section && section.classList.contains('sidebar-open')) {
      closeFilters(section);
    }
  });
})();
